/**
 * TableRenderer — renders `table` and `list` artifacts.
 *
 * Shared payload shape (`TablePayload`) covers both types. The renderer
 * branches on `payload.format`:
 *   - "structured"  → HTML table with click-to-sort headers
 *   - "markdown"    → ReactMarkdown body
 *   - missing/unknown → markdown fallback or empty state
 *
 * Spec: .workspace-spec/WORKSPACE_CONTRACT.md (TablePayload + Actions)
 */
import { useMemo, useState } from "react"
import type { ReactNode } from "react"

import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"

import type { Artifact, ArtifactAction, TableColumn, TablePayload } from "@/api/models"

import { ChevronDown, ChevronUp } from "@/components/icons"
import { cn } from "@/lib/utils"

import { ArtifactFrame } from "./ArtifactFrame"

export interface TableRendererProps {
  artifact: Artifact
  versionChain?: Artifact[]
  onAction?: (actionId: string, artifact: Artifact) => void
  onSelectVersion?: (artifactId: string) => void
  onViewTrace?: (missionId: string) => void
  onShowHistory?: () => void
}

/** Default actions surfaced when `artifact.actions` is empty. */
function defaultActions(payload: TablePayload): ArtifactAction[] {
  const base: ArtifactAction[] = [{ id: "copy", label: "Copy" }]
  if (payload.format === "structured") {
    base.push({ id: "download", label: "Download CSV" })
  } else {
    base.push({ id: "download", label: "Download" })
  }
  return base
}

type SortDirection = "asc" | "desc" | null

interface SortState {
  key: string | null
  direction: SortDirection
}

function compareValues(a: unknown, b: unknown, type: TableColumn["type"]): number {
  // Nullish values sort to the bottom regardless of direction.
  const aNil = a === null || a === undefined || a === ""
  const bNil = b === null || b === undefined || b === ""
  if (aNil && bNil) return 0
  if (aNil) return 1
  if (bNil) return -1

  if (type === "number") {
    const an = typeof a === "number" ? a : Number(a)
    const bn = typeof b === "number" ? b : Number(b)
    if (Number.isNaN(an) && Number.isNaN(bn)) return 0
    if (Number.isNaN(an)) return 1
    if (Number.isNaN(bn)) return -1
    return an - bn
  }
  if (type === "date") {
    const at = Date.parse(String(a))
    const bt = Date.parse(String(b))
    if (Number.isNaN(at) && Number.isNaN(bt)) return 0
    if (Number.isNaN(at)) return 1
    if (Number.isNaN(bt)) return -1
    return at - bt
  }
  // text default — stable locale compare.
  return String(a).localeCompare(String(b))
}

function StructuredTable({
  columns,
  rows,
  compact,
}: {
  columns: TableColumn[]
  rows: Record<string, unknown>[]
  compact: boolean
}) {
  const [sort, setSort] = useState<SortState>({ key: null, direction: null })

  const sortedRows = useMemo(() => {
    if (!sort.key || !sort.direction) return rows
    const col = columns.find((c) => c.key === sort.key)
    if (!col) return rows
    const colType = col.type ?? "text"
    const dir = sort.direction === "asc" ? 1 : -1
    // Stable sort: pair-with-index then compare.
    const indexed = rows.map((r, i) => ({ r, i }))
    indexed.sort((a, b) => {
      const cmp = compareValues(a.r[col.key], b.r[col.key], colType)
      if (cmp !== 0) return cmp * dir
      return a.i - b.i
    })
    return indexed.map((x) => x.r)
  }, [rows, columns, sort])

  function cycleSort(key: string) {
    setSort((prev) => {
      if (prev.key !== key) return { key, direction: "asc" }
      if (prev.direction === "asc") return { key, direction: "desc" }
      if (prev.direction === "desc") return { key: null, direction: null }
      return { key, direction: "asc" }
    })
  }

  const showHeader = !(columns.length === 1 && columns[0].key === "item")

  return (
    <div className="overflow-x-auto" data-testid="table-renderer-structured">
      <table className="w-full text-sm">
        {showHeader ? (
          <thead>
            <tr className="border-b">
              {columns.map((col) => {
                const isActive = sort.key === col.key && sort.direction !== null
                let ariaSort: "none" | "ascending" | "descending" = "none"
                if (isActive) {
                  ariaSort = sort.direction === "asc" ? "ascending" : "descending"
                }
                let chevron: ReactNode = null
                if (isActive && sort.direction === "asc") {
                  chevron = (
                    <ChevronUp
                      aria-hidden
                      className="size-3"
                      data-testid={`table-sort-asc-${col.key}`}
                    />
                  )
                } else if (isActive && sort.direction === "desc") {
                  chevron = (
                    <ChevronDown
                      aria-hidden
                      className="size-3"
                      data-testid={`table-sort-desc-${col.key}`}
                    />
                  )
                }
                return (
                  <th
                    key={col.key}
                    aria-sort={ariaSort}
                    scope="col"
                    className={cn(
                      "text-muted-foreground px-3 py-2 text-left text-xs font-medium select-none",
                      compact && "py-1.5"
                    )}
                  >
                    <button
                      className="hover:text-foreground inline-flex items-center gap-1"
                      data-testid={`table-header-${col.key}`}
                      type="button"
                      onClick={() => cycleSort(col.key)}
                    >
                      <span>{col.label}</span>
                      {chevron}
                    </button>
                  </th>
                )
              })}
            </tr>
          </thead>
        ) : null}
        <tbody>
          {sortedRows.map((row, idx) => (
            <tr
              key={idx}
              className="hover:bg-muted/50 border-b last:border-b-0"
              data-testid="table-row"
            >
              {columns.map((col) => {
                const v = row[col.key]
                let display = ""
                if (v !== null && v !== undefined) {
                  display = typeof v === "object" ? JSON.stringify(v) : String(v)
                }
                return (
                  <td
                    key={col.key}
                    className={cn("px-3 py-2 align-top", compact && "py-1.5")}
                    data-testid={`table-cell-${col.key}`}
                  >
                    {display}
                  </td>
                )
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

function MarkdownBody({ body }: { body: string }) {
  return (
    <div
      className="prose prose-sm dark:prose-invert max-w-none"
      data-testid="table-renderer-markdown"
    >
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{body}</ReactMarkdown>
    </div>
  )
}

function EmptyState() {
  return (
    <div
      className="text-muted-foreground py-6 text-center text-sm"
      data-testid="table-renderer-empty"
    >
      No data
    </div>
  )
}

export function TableRenderer({
  artifact,
  versionChain,
  onAction,
  onSelectVersion,
  onViewTrace,
  onShowHistory,
}: TableRendererProps) {
  const payload = artifact.payload as TablePayload
  const compact = artifact.type === "list"

  // Inject default actions when the backend didn't supply any. The frame is
  // responsible for surfacing them; we just enrich the artifact for that pass.
  const effectiveActions =
    artifact.actions && artifact.actions.length > 0 ? artifact.actions : defaultActions(payload)
  const effectiveArtifact: Artifact = { ...artifact, actions: effectiveActions }

  let body: ReactNode
  if (
    payload.format === "structured" &&
    payload.columns &&
    payload.columns.length > 0 &&
    payload.rows
  ) {
    body = <StructuredTable columns={payload.columns} compact={compact} rows={payload.rows} />
  } else if (payload.format === "markdown" && payload.body) {
    body = <MarkdownBody body={payload.body} />
  } else if (payload.body) {
    // Format unset / unknown but we have a markdown body → fall back to it.
    body = <MarkdownBody body={payload.body} />
  } else {
    body = <EmptyState />
  }

  return (
    <ArtifactFrame
      artifact={effectiveArtifact}
      versionChain={versionChain}
      onAction={onAction}
      onSelectVersion={onSelectVersion}
      onShowHistory={onShowHistory}
      onViewTrace={onViewTrace}
    >
      {body}
    </ArtifactFrame>
  )
}
